import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plug, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusPill } from "@/components/StatusPill";
import { api, apiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/constants";

const STATUS_STYLES = {
  verbunden: { label: "Verbunden", color: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  testmodus: { label: "Testmodus", color: "bg-sky-500/15 text-sky-300 border-sky-500/30" },
  nicht_verbunden: { label: "Nicht verbunden", color: "bg-slate-500/20 text-slate-300 border-slate-500/30" },
  fehler: { label: "Fehler", color: "bg-red-500/15 text-red-300 border-red-500/30" },
  demnaechst: { label: "Demnächst", color: "bg-violet-400/15 text-violet-300 border-violet-400/30" },
};

export default function IntegrationsPage() {
  const [items, setItems] = useState(null);
  const [dealalertStatus, setDealalertStatus] = useState(null);
  const [active, setActive] = useState(null);
  const [creds, setCreds] = useState({ api_key: "", account_id: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/integrations").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    // DealAlert: rein serverseitige Konfiguration - Status liefert nur configured (+ environment)
    api.get("/integrations/dealalert/status").then((r) => setDealalertStatus(r.data)).catch(() => setDealalertStatus(null));
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/integrations/${active.id}/connect`, { credentials: creds });
      toast.success(r.data.message);
      setActive(null);
      setCreds({ api_key: "", account_id: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display text-2xl lg:text-3xl font-bold">Integrationen</h1>
        <p className="text-sm text-muted-foreground">
          Modulare Adapter-Architektur. Keine Live-Verbindung wird vorgetäuscht – Testmodus ist klar gekennzeichnet.
        </p>
      </div>
      {!items ? (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-40" />)}</div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="integrations-grid">
          {items.map((it) => {
            let st = STATUS_STYLES[it.status] || STATUS_STYLES.nicht_verbunden;
            let dealalertHint = null;
            if (it.id === "dealalert") {
              // Nur Statusanzeige - Secrets werden ausschließlich serverseitig verwaltet
              if (dealalertStatus?.configured) {
                st = { label: `Konfiguriert (${dealalertStatus.environment === "sandbox" ? "Sandbox" : dealalertStatus.environment})`, color: "bg-sky-500/15 text-sky-300 border-sky-500/30" };
                dealalertHint = "Serverseitig konfiguriert – noch nicht aktiviert.";
              } else {
                st = { label: "Nicht konfiguriert", color: "bg-slate-500/20 text-slate-300 border-slate-500/30" };
                dealalertHint = "Wird serverseitig über Plattform-Secrets konfiguriert.";
              }
            }
            return (
              <Card key={it.id} className="flex flex-col card-hover" data-testid={`integration-card-${it.id}`}>
                <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-sm flex items-center gap-2"><Plug className="h-4 w-4 text-primary" /> {it.name}</CardTitle>
                  <StatusPill label={st.label} color={st.color} testId={it.id === "dealalert" ? "dealalert-status-pill" : "status-pill"} />
                </CardHeader>
                <CardContent className="flex flex-col flex-1 gap-3">
                  <p className="text-xs text-muted-foreground flex-1">{it.description}</p>
                  {dealalertHint && (
                    <p className="text-[10px] text-muted-foreground" data-testid="dealalert-config-hint">{dealalertHint}</p>
                  )}
                  {it.connection?.logs?.length > 0 && (
                    <div className="rounded-lg bg-secondary/40 border border-border p-2 space-y-1">
                      <div className="text-[10px] font-medium text-muted-foreground">Connection Log</div>
                      {it.connection.logs.map((l, i) => (
                        <div key={i} className="text-[10px] text-muted-foreground">{fmtDateTime(l.at)} – {l.event}</div>
                      ))}
                    </div>
                  )}
                  <Button variant="outline" size="sm" disabled={it.status === "demnaechst" || it.id === "dealalert"} data-testid={`integration-connect-${it.id}`}
                    onClick={() => setActive(it)}>
                    {it.id === "dealalert" ? "Wird später gemeinsam eingerichtet" : it.status === "demnaechst" ? "Demnächst verfügbar" : it.status === "verbunden" || it.status === "testmodus" ? "Zugangsdaten verwalten" : "Verbinden"}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{active?.name} verbinden</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">
            Zugangsdaten werden sicher serverseitig gespeichert (maskiert). Die Verbindung läuft im <strong>Testmodus</strong>,
            bis eine echte API-Freischaltung des Anbieters vorliegt.
          </p>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>API-Schlüssel / Benutzername</Label>
              <Input value={creds.api_key} onChange={(e) => setCreds((c) => ({ ...c, api_key: e.target.value }))} data-testid="integration-apikey-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Account-/Händler-ID (optional)</Label>
              <Input value={creds.account_id} onChange={(e) => setCreds((c) => ({ ...c, account_id: e.target.value }))} data-testid="integration-account-input" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={connect} disabled={busy || !creds.api_key} data-testid="integration-connect-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Im Testmodus verbinden"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
